import { StarIcon } from '@/components/icon';
import { ekDomain, ROUTES } from '@/configs/constants';
import { LANGUAGES_SUPPORTED } from '@/types/enums';
import { isPathEk } from '@/utils/routes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

interface BikeItemProps {
    bike: any,
    locationId: string | number | undefined
}
export default observer(function BikeItem({ bike, locationId }: BikeItemProps) {
    // hook
    const { t, i18n: { language } } = useTranslation();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // functions
    const getUrlBikeDetail = (): string => {
        return isEk ?
            `/${ekDomain}/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${locationId}/${ROUTES.bikeBookingDetail.href}/${bike?.id}` :
            `/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${locationId}/${ROUTES.bikeBookingDetail.href}/${bike?.id}`;
    };

    const getBikeManufacture = (bike: any, isEk: boolean): string => {
        if (isEk) {
            return bike?.bikeManufacture?.id;
        }

        if (language === LANGUAGES_SUPPORTED.Japanese) {
            return bike?.bikeManufacture?.name_jp || '--';
        } else {
            return bike?.bikeManufacture?.name || '--';
        }
    };

    // const
    const imgUrl = bike?.vehicleImageRight?.filePath ??
        bike?.vehicleImageLeft?.filePath ??
        bike?.vehicleImageBefore?.filePath ??
        bike?.vehicleImageAfter?.filePath ?? '';

    return (
        <div className='bg-background px-[20px] py-[30px] border border-border-gray2 rounded-[9.255px] flex flex-col gap-[16px]'>
            {/* Image */}
            <div className='flex-none w-full relative'>
                <Link target='_blank' rel='noopener noreferrer' to={getUrlBikeDetail()}>
                    <div
                        className='h-[180px] w-full bg-no-repeat bg-center bg-cover bg-background-img rounded-[7.712px]'
                        style={{
                            backgroundImage: `url(${imgUrl})`
                        }}
                    />
                </Link>

                {
                    bike?.isRecommendation && (
                        <div className='bg-primaryWeb h-[30px] rounded-[100px] px-[20px] py-[8px] absolute left-2.5 top-2.5 flex items-center justify-center gap-[5px]'>
                            <StarIcon />
                            <span className='text-[12px] leading-[1] text-customWhite'>{t('words_title.recomment')}</span>
                        </div>
                    )
                }
            </div>

            {/* Bike name */}
            <Link target='_blank' rel='noopener noreferrer' to={getUrlBikeDetail()}>
                <div className='box-title line-clamp-1'>{bike?.bikeModel || '--'}</div>
            </Link>

            {/* Content */}
            <div className='leading-lh-nor14 text-fs-mid14 font-normal'>
                <div className='grid grid-cols-6 items-center border-x border-b border-border-gray2 border-t'>
                    <div className='col-span-3 px-[min(20px,2.7vw)] py-[min(10px,2vw)] flex break-words items-center justify-end bg-[#F8F7F1] text-left border-r border-border-gray2 h-full'>
                        {t('field.manufacturer')}
                    </div>
                    <div className='col-span-3 px-[min(10px,2vw)] py-[min(10px,2vw)] flex items-center justify-center text-center'>
                        {getBikeManufacture(bike, isEk)}
                    </div>
                </div>
                {!isEk && (
                    <>
                        <div className='grid grid-cols-6 items-center border-x border-b border-border-gray2'>
                            <div className='col-span-3 px-[min(20px,2.7vw)] py-[min(10px,2vw)] flex break-words items-center justify-end bg-[#F8F7F1] text-left border-r border-border-gray2 h-full'>
                                {t('field.engine_size')}
                            </div>
                            <div className='col-span-3 px-[min(10px,2vw)] py-[min(10px,2vw)] flex items-center justify-center text-center'>
                                {bike?.bikeCapacity?.name || '--'}
                            </div>
                        </div>
                        <div className='grid grid-cols-6 items-center border-x border-b border-border-gray2'>
                            <div className='col-span-3 px-[min(20px,2.7vw)] py-[min(10px,2vw)] flex break-words items-center justify-end bg-[#F8F7F1] text-left border-r border-border-gray2 h-full'>
                                {t('field.gearbox')}
                            </div>
                            <div className='col-span-3 px-[min(10px,2vw)] py-[min(10px,2vw)] flex items-center justify-center text-center'>
                                {bike.bikeTransmission?.toUpperCase()}
                            </div>
                        </div>
                    </>
                )}
                <div className='grid grid-cols-6 items-center border-x border-b border-border-gray2'>
                    <div className='col-span-3 px-[min(20px,2.7vw)] py-[min(10px,2vw)] flex break-words items-center justify-end bg-[#F8F7F1] text-left border-r border-border-gray2 h-full'>
                        {t('field.type')}
                    </div>
                    <div className='col-span-3 px-[min(10px,2vw)] py-[min(10px,2vw)] flex items-center justify-center text-center'>
                        {language === LANGUAGES_SUPPORTED.Japanese ? (bike?.bikeType.name_jp || '--') : (bike?.bikeType.name || '--')}
                    </div>
                </div>
            </div>

        </div>
    );
});
